'use client';

import { cn } from '@/layers/support/cn';
import type { User } from '@/types/models';
import { LayoutDashboard, LogIn, LogOut, User as UserIcon } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import Logo from './Logo';
import type { NavbarVariant } from './navbarShared';
import { getNavbarContainerClass } from './navbarShared';

export interface NavbarMenuItemData {
  href: string;
  label: string;
  isActive: boolean;
}

export interface NavbarRendererLabels {
  loginOrSignUp: string;
  loginOrCreateAccount: string;
  logout: string;
  myProfile: string;
  /** CTA shown to an authenticated user on marketing routes. */
  goToDashboard?: string;
}

export interface NavbarRendererProps {
  variant?: NavbarVariant;
  /** Extra class to apply to the fixed navbar root (e.g. 'marketing-navbar'). */
  className?: string;
  /** Either 'top-0' or PROMO_BANNER_NAVBAR_OFFSET_CLASS (or any other top-* class). */
  topClassName?: string;
  menuItems: NavbarMenuItemData[];
  authHref: string;
  labels: NavbarRendererLabels;
  user: User | null;
  onLogout?: () => Promise<void> | void;
  /**
   * When set (marketing navbar), an authenticated user sees a single
   * "Go to Dashboard" CTA routing here instead of the My Profile / Logout pair.
   */
  dashboardHref?: string;
}

export default function NavbarRenderer({
  variant = 'default',
  className,
  topClassName = 'top-0',
  menuItems,
  authHref,
  labels,
  user,
  onLogout,
  dashboardHref,
}: NavbarRendererProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const pillItemClass = (isActive: boolean) =>
    cn(
      'rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors',
      isActive ? 'bg-default-700' : 'bg-default-900 hover:bg-default-800',
    );

  const borderedButtonClass =
    variant === 'dark'
      ? 'text-white border-white/40 hover:bg-white/10'
      : 'text-primary border-primary/40 hover:bg-primary/5';

  return (
    <header
      className={cn(
        'p-3 fixed left-0 right-0 z-50 px-4 sm:px-6',
        getNavbarContainerClass(variant),
        topClassName,
        className,
      )}
    >
      <nav className="w-full">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo className="flex" variant={variant} />
          </div>

          <div className="hidden sm:flex flex-1 justify-center">
            <div className="flex items-center rounded-xl bg-primary p-1">
              {menuItems.map(item => (
                <Link key={item.href} href={item.href} className={pillItemClass(item.isActive)}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-4">
              {user && dashboardHref ? (
                <Link
                  href={dashboardHref}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                    borderedButtonClass,
                  )}
                >
                  <LayoutDashboard size={18} />
                  {labels.goToDashboard ?? 'Go to Dashboard'}
                </Link>
              ) : user ? (
                <>
                  <Link
                    href="/users/profile"
                    className={cn(
                      'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                      borderedButtonClass,
                    )}
                  >
                    <UserIcon size={18} />
                    {labels.myProfile}
                  </Link>
                  <button
                    type="button"
                    onClick={async () => {
                      if (onLogout) await onLogout();
                    }}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                      borderedButtonClass,
                    )}
                  >
                    <LogOut size={18} />
                    {labels.logout}
                  </button>
                </>
              ) : (
                <Link
                  href={authHref}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                    borderedButtonClass,
                  )}
                >
                  <LogIn size={18} />
                  {labels.loginOrSignUp}
                </Link>
              )}
            </div>

            <button
              type="button"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="marketing-mobile-menu"
              className={cn(
                'sm:hidden rounded-xl px-3 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                variant === 'dark'
                  ? 'text-white focus-visible:outline-white'
                  : 'text-primary focus-visible:outline-primary',
              )}
              onClick={() => setIsMobileMenuOpen(v => !v)}
            >
              <span className="block h-0.5 w-6 bg-current mb-1.5" />
              <span className="block h-0.5 w-6 bg-current mb-1.5" />
              <span className="block h-0.5 w-6 bg-current" />
            </button>
          </div>
        </div>

        {isMobileMenuOpen ? (
          <div
            id="marketing-mobile-menu"
            className={cn(
              'sm:hidden mt-3 rounded-xl p-4 shadow-lg',
              variant === 'dark' ? 'bg-primary text-white' : 'bg-white text-primary',
            )}
          >
            <div className="flex flex-col gap-3">
              {menuItems.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn('text-base', item.isActive && 'font-semibold')}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              <div className="mt-2">
                {user && dashboardHref ? (
                  <Link
                    href={dashboardHref}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                      borderedButtonClass,
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LayoutDashboard size={18} />
                    {labels.goToDashboard ?? 'Go to Dashboard'}
                  </Link>
                ) : user ? (
                  <div className="flex flex-col gap-2">
                    <Link
                      href="/users/profile"
                      className={cn(
                        'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                        borderedButtonClass,
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <UserIcon size={18} />
                      {labels.myProfile}
                    </Link>
                    <button
                      type="button"
                      onClick={async () => {
                        setIsMobileMenuOpen(false);
                        if (onLogout) await onLogout();
                      }}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                        borderedButtonClass,
                      )}
                    >
                      <LogOut size={18} />
                      {labels.logout}
                    </button>
                  </div>
                ) : (
                  <Link
                    href={authHref}
                    className={cn(
                      'inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors',
                      borderedButtonClass,
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LogIn size={18} />
                    {labels.loginOrCreateAccount}
                  </Link>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </nav>
    </header>
  );
}
